import {
  createContext,
  CSSProperties,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { buildClasses } from "../utils/buildClasses";

type FloatingTooltip = ReturnType<typeof useFloating>;
const ChartTooltipContext = createContext<FloatingTooltip | null>(null);

/** Tracks the pointer in viewport coordinates, outside any chart-library positioning. */
export function RyeChartTooltipProvider({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}): JSX.Element {
  const floating = useFloating({
    open: true,
    placement: "right",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [offset(14), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  function trackPointer(event: MouseEvent<HTMLDivElement>) {
    const { clientX: x, clientY: y, currentTarget } = event;
    floating.refs.setPositionReference({
      contextElement: currentTarget,
      getBoundingClientRect: () => ({
        x,
        y,
        top: y,
        bottom: y,
        left: x,
        right: x,
        width: 0,
        height: 0,
      }),
    });
  }

  return (
    <ChartTooltipContext.Provider value={floating}>
      <div
        className={className}
        style={style}
        onMouseOverCapture={trackPointer}
        onMouseMoveCapture={trackPointer}
      >
        {children}
      </div>
    </ChartTooltipContext.Provider>
  );
}

/** Chart libraries may control visibility; Floating UI owns placement and layering. */
export function RyeChartTooltip({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const floating = useContext(ChartTooltipContext);
  const [animateMovement, setAnimateMovement] = useState(false);
  const isPositioned = floating?.isPositioned ?? false;

  useEffect(() => {
    if (!isPositioned) {
      setAnimateMovement(false);
      return;
    }
    // Place the first frame immediately; only subsequent pointer movement eases.
    const frame = requestAnimationFrame(() => setAnimateMovement(true));
    return () => cancelAnimationFrame(frame);
  }, [isPositioned]);

  if (!floating)
    throw new Error("RyeChartTooltip requires RyeChartTooltipProvider");

  return (
    <FloatingPortal>
      <div
        ref={floating.refs.setFloating}
        role="tooltip"
        style={{
          ...floating.floatingStyles,
          visibility: floating.isPositioned ? "visible" : "hidden",
        }}
        className={buildClasses(
          "z-[2147483647]",
          "pointer-events-none",
          "max-w-[calc(100vw-16px)]",
          "break-words",
          {
            if: animateMovement,
            then: buildClasses(
              "motion-safe:transition-transform",
              "motion-safe:duration-200",
              "motion-safe:ease-out",
            ),
          },
        )}
      >
        {children}
      </div>
    </FloatingPortal>
  );
}
